import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { trailLogsAPI } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, MapPin, Calendar, Mountain, Ruler, Clock, Trash2,
  Upload, FileText, Loader2, Map, Pencil,
} from "lucide-react";

// Leaflet CSS must be imported
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import { gpx as gpxToGeoJSON } from "@tmcw/togeojson";

const DIFFICULTIES = ["Easy", "Moderate", "Difficult", "Expert", "Extreme"];

function GpxMap({ gpxData }) {
  const [geoJson, setGeoJson] = useState(null);

  useEffect(() => {
    if (!gpxData) return;
    try {
      const parser = new DOMParser();
      const gpxDoc = parser.parseFromString(gpxData, "text/xml");
      const converted = gpxToGeoJSON(gpxDoc);
      setGeoJson(converted);
    } catch (err) {
      console.error("Failed to parse GPX:", err);
    }
  }, [gpxData]);

  if (!geoJson || !geoJson.features?.length) return null;

  // Calculate bounds from GeoJSON
  const allCoords = [];
  geoJson.features.forEach((f) => {
    if (f.geometry.type === "LineString") {
      f.geometry.coordinates.forEach(([lng, lat]) => allCoords.push([lat, lng]));
    } else if (f.geometry.type === "MultiLineString") {
      f.geometry.coordinates.forEach((line) =>
        line.forEach(([lng, lat]) => allCoords.push([lat, lng]))
      );
    } else if (f.geometry.type === "Point") {
      const [lng, lat] = f.geometry.coordinates;
      allCoords.push([lat, lng]);
    }
  });

  const center = allCoords.length > 0
    ? [
        allCoords.reduce((s, c) => s + c[0], 0) / allCoords.length,
        allCoords.reduce((s, c) => s + c[1], 0) / allCoords.length,
      ]
    : [39.8283, -98.5795];

  return (
    <div className="rounded-lg overflow-hidden border border-border" style={{ height: 350 }}>
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <GeoJSON
          data={geoJson}
          style={{ color: "#f97316", weight: 3, opacity: 0.8 }}
        />
        <FitBounds coords={allCoords} />
      </MapContainer>
    </div>
  );
}

function FitBounds({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (coords.length > 1) {
      map.fitBounds(coords, { padding: [30, 30] });
    }
  }, [coords, map]);
  return null;
}

export default function TrailLogs() {
  const { id: vehicleId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editLog, setEditLog] = useState(null);
  const [viewLog, setViewLog] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [dragOver, setDragOver] = useState(null); // log id being dragged over

  useEffect(() => {
    loadLogs();
  }, [vehicleId]);

  const loadLogs = async () => {
    try {
      const res = await trailLogsAPI.list(vehicleId);
      setLogs(res.data);
    } catch {
      toast.error("Failed to load trail logs");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await trailLogsAPI.create(vehicleId);
      setLogs([res.data, ...logs]);
      setEditLog(res.data);
      toast.success("Trail log created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create trail log");
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    if (!editLog) return;
    try {
      const res = await trailLogsAPI.update(editLog.id, {
        trail_name: editLog.trail_name,
        trail_date: editLog.trail_date,
        difficulty: editLog.difficulty,
        distance_miles: editLog.distance_miles,
        duration_hours: editLog.duration_hours,
        location: editLog.location,
        latitude: editLog.latitude,
        longitude: editLog.longitude,
        notes: editLog.notes,
      });
      setLogs(logs.map((l) => (l.id === editLog.id ? res.data : l)));
      setEditLog(null);
      toast.success("Trail log saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleUploadGpx = async (logId, file) => {
    setUploading(true);
    try {
      const res = await trailLogsAPI.uploadGpx(logId, file);
      setLogs(logs.map((l) => (l.id === logId ? res.data : l)));
      if (editLog?.id === logId) setEditLog(res.data);
      if (viewLog?.id === logId) setViewLog(res.data);
      toast.success("GPX file uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload GPX");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (logId) => {
    try {
      await trailLogsAPI.delete(logId);
      setLogs(logs.filter((l) => l.id !== logId));
      setDeleteConfirm(null);
      toast.success("Trail log deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const difficultyColor = (d) => {
    const colors = {
      Easy: "bg-green-500/20 text-green-400 border-green-500/30",
      Moderate: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      Difficult: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      Expert: "bg-red-500/20 text-red-400 border-red-500/30",
      Extreme: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    };
    return colors[d] || "bg-zinc-500/20 text-zinc-400";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Mountain className="w-5 h-5 text-primary" />
                Trail Logs
              </h1>
              <p className="text-xs text-muted-foreground">{logs.length} trails logged</p>
            </div>
          </div>
          <Button onClick={handleCreate} disabled={creating} data-testid="add-trail-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Log Trail
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {logs.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Mountain className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">No trails logged yet</p>
            <p className="text-sm">Click "Log Trail" to record your first adventure.</p>
          </div>
        )}

        {logs.map((log) => (
          <Card
            key={log.id}
            data-testid={`trail-log-${log.id}`}
            className={`transition-all ${dragOver === log.id ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOver(log.id);
            }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(null);
              const file = e.dataTransfer.files?.[0];
              if (file && file.name.toLowerCase().endsWith(".gpx")) {
                handleUploadGpx(log.id, file);
              } else {
                toast.error("Only .gpx files are accepted");
              }
            }}
          >
            <CardContent className="pt-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-base">{log.trail_name}</h3>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {log.trail_date ? new Date(log.trail_date + "T00:00:00").toLocaleDateString() : "No date"}
                    </span>
                    {log.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {log.location}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${difficultyColor(log.difficulty)}`}>
                    {log.difficulty}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                {log.distance_miles > 0 && (
                  <span className="flex items-center gap-1">
                    <Ruler className="w-3 h-3" /> {log.distance_miles} mi
                  </span>
                )}
                {log.duration_hours > 0 && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {log.duration_hours}h
                  </span>
                )}
                {log.gpx_data && (
                  <span className="flex items-center gap-1 text-primary">
                    <Map className="w-3 h-3" /> GPX Route
                  </span>
                )}
              </div>

              {log.notes && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{log.notes}</p>}

              {/* GPX Map Preview or Drop Zone */}
              {dragOver === log.id && !log.gpx_data && (
                <div className="border-2 border-dashed border-primary/50 rounded-lg p-6 text-center bg-primary/5 mb-3">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-primary/60" />
                  <p className="text-sm text-primary/80">Drop GPX file here</p>
                </div>
              )}
              {log.gpx_data && <GpxMap gpxData={log.gpx_data} />}

              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setViewLog(log)} data-testid={`view-trail-${log.id}`}>
                  <Map className="w-3.5 h-3.5 mr-1" /> View Details
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditLog({...log})} data-testid={`edit-trail-${log.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    fileInputRef.current?.setAttribute("data-log-id", log.id);
                    fileInputRef.current?.click();
                  }}
                  disabled={uploading}
                >
                  <Upload className="w-3.5 h-3.5 mr-1" /> {log.gpx_data ? "Replace GPX" : "Upload GPX"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteConfirm(log.id)}
                  className="ml-auto text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </main>

      {/* Hidden file input for GPX upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".gpx"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          const logId = fileInputRef.current?.getAttribute("data-log-id");
          if (file && logId) handleUploadGpx(logId, file);
          e.target.value = "";
        }}
      />

      {/* Edit Dialog */}
      <Dialog open={!!editLog} onOpenChange={(open) => !open && setEditLog(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Trail Log</DialogTitle>
          </DialogHeader>
          {editLog && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Trail Name</Label>
                <Input
                  value={editLog.trail_name}
                  onChange={(e) => setEditLog({ ...editLog, trail_name: e.target.value })}
                  data-testid="edit-trail-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={editLog.trail_date || ""}
                    onChange={(e) => setEditLog({ ...editLog, trail_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Difficulty</Label>
                  <Select value={editLog.difficulty} onValueChange={(v) => setEditLog({ ...editLog, difficulty: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DIFFICULTIES.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Distance (miles)</Label>
                  <Input
                    type="number"
                    value={editLog.distance_miles || ""}
                    onChange={(e) => setEditLog({ ...editLog, distance_miles: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration (hours)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={editLog.duration_hours || ""}
                    onChange={(e) => setEditLog({ ...editLog, duration_hours: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={editLog.location || ""}
                  onChange={(e) => setEditLog({ ...editLog, location: e.target.value })}
                  placeholder="e.g., Moab, UT"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={editLog.latitude || ""}
                    onChange={(e) => setEditLog({ ...editLog, latitude: parseFloat(e.target.value) || null })}
                    placeholder="38.5733"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={editLog.longitude || ""}
                    onChange={(e) => setEditLog({ ...editLog, longitude: parseFloat(e.target.value) || null })}
                    placeholder="-109.5498"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editLog.notes || ""}
                  onChange={(e) => setEditLog({ ...editLog, notes: e.target.value })}
                  placeholder="Trail conditions, obstacles, highlights..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLog(null)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-trail-btn">Save Trail Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Detail Dialog */}
      <Dialog open={!!viewLog} onOpenChange={(open) => !open && setViewLog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mountain className="w-5 h-5 text-primary" />
              {viewLog?.trail_name}
            </DialogTitle>
            <DialogDescription>
              {viewLog?.trail_date && new Date(viewLog.trail_date + "T00:00:00").toLocaleDateString()}
              {viewLog?.location && ` — ${viewLog.location}`}
            </DialogDescription>
          </DialogHeader>
          {viewLog && (
            <div className="space-y-4 py-2">
              <div className="flex flex-wrap gap-3">
                <Badge className={difficultyColor(viewLog.difficulty)}>{viewLog.difficulty}</Badge>
                {viewLog.distance_miles > 0 && (
                  <Badge variant="outline"><Ruler className="w-3 h-3 mr-1" /> {viewLog.distance_miles} miles</Badge>
                )}
                {viewLog.duration_hours > 0 && (
                  <Badge variant="outline"><Clock className="w-3 h-3 mr-1" /> {viewLog.duration_hours} hours</Badge>
                )}
              </div>
              {viewLog.notes && <p className="text-sm">{viewLog.notes}</p>}
              {viewLog.gpx_data && (
                <div>
                  <p className="text-sm font-medium mb-2 flex items-center gap-1">
                    <FileText className="w-4 h-4" /> GPX Route — {viewLog.gpx_filename}
                  </p>
                  <GpxMap gpxData={viewLog.gpx_data} />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Trail Log?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-trail">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
